import SectionContainer from './SectionContainer';
import SectionHeader from './SectionHeader';

// Centralized skeleton fallbacks that preserve layout and use theme tokens
export const EditorSkeleton = () => (
  <div className="min-h-[400px] bg-surface border-2 border-dashed border-border">
    <div className="p-4 space-y-3">
      <div className="h-4 bg-card rounded w-3/4 animate-pulse"></div>
      <div className="h-4 bg-card rounded w-1/2 animate-pulse"></div>
      <div className="h-32 bg-card rounded animate-pulse"></div>
      <div className="h-4 bg-card rounded w-2/3 animate-pulse"></div>
    </div>
  </div>
);

export const ChartsSkeleton = () => (
  <div className="h-64 bg-surface rounded-lg animate-pulse flex items-center justify-center">
    <div className="text-textMuted text-sm">Loading charts...</div>
  </div>
);

export const AssetsSkeleton = () => (
  <SectionContainer variant="bare">
    <SectionHeader title="Live Assets" href="https://docs.google.com/live-assets-master">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-12 bg-surface rounded animate-pulse"></div>
          <div className="h-8 w-16 bg-surface rounded animate-pulse"></div>
        </div>
      </div>
    </SectionHeader>
    <div className="mt-6">
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3].map(i => (
      <div key={i} className="h-48 bg-surface rounded-lg animate-pulse"></div>
    ))}
  </div>
    </div>
  </SectionContainer>
);

export const CreatorsSkeleton = () => (
  <SectionContainer variant="bare">
    <div className="flex items-center justify-between mb-6">
      <SectionHeader 
        title="Recommended Creators" 
        href="https://docs.google.com/creators-master"
      />
        </div>
    {/* Toggles for sorting/filtering */}
    <div className="flex items-center gap-4 mb-6 z-10">
      <div className="flex gap-2">
        <div className="h-8 w-24 bg-surface rounded animate-pulse"></div>
        <div className="h-8 w-32 bg-surface rounded animate-pulse"></div>
      </div>
      <div className="ml-auto h-8 w-20 bg-surface rounded animate-pulse"></div>
    </div>
    <div className="min-h-[220px]">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" style={{ gap: 'clamp(1rem, 2vw, 1.5rem)' }}>
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-surface rounded-[30px] p-6 animate-pulse h-[400px]">
            <div className="h-48 bg-card rounded-xl mb-4"></div>
            <div className="h-4 bg-card rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-card rounded w-1/2"></div>
      </div>
    ))}
  </div>
    </div>
  </SectionContainer>
);

export const FunnelSkeleton = () => (
  <SectionContainer variant="bare">
    <SectionHeader 
      title="Funnel" 
      href="https://docs.google.com/funnel-master"
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 mt-6" style={{ gap: 'clamp(1rem, 2vw, 1.5rem)' }}>
    {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-surface rounded-2xl p-6 animate-pulse">
        <div className="h-4 bg-card rounded w-1/2 mb-2"></div>
        <div className="h-8 bg-card rounded w-3/4"></div>
        <div className="h-3 bg-card rounded w-1/3 mt-2"></div>
      </div>
    ))}
  </div>
  </SectionContainer>
);

export const NotesSkeleton = () => (
  <div className="notes-panel elevation-card">
    {/* Editor skeleton - matches RichTextEditor height (~400px) */}
    <div className="notes-editor">
      <div className="notes-editor-body">
        <div className="min-h-[400px] p-6 space-y-4">
          {/* Toolbar skeleton */}
          <div className="h-10 bg-surface rounded animate-pulse mb-4"></div>
          {/* Editor content skeleton */}
          <div className="space-y-3">
            <div className="h-4 bg-card rounded w-3/4 animate-pulse"></div>
            <div className="h-4 bg-card rounded w-1/2 animate-pulse"></div>
            <div className="h-32 bg-card rounded animate-pulse"></div>
            <div className="h-4 bg-card rounded w-2/3 animate-pulse"></div>
            <div className="h-4 bg-card rounded w-5/6 animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const SpendSkeleton = () => (
  <div className="w-full magazine-section">
    <div className="elevation-card overflow-hidden modern-card no-hover-lift" data-section="campaign-spend">
      {/* Header skeleton - matches actual header height */}
      <div className="px-6 py-6 card-header">
        <div className="h-8 bg-surface rounded animate-pulse w-48 mb-4"></div>
      </div>
      {/* Table skeleton - matches VirtualizedSpendTable height (400px container) */}
      <div className="px-4 py-3 min-h-[400px] space-y-2">
        {/* Table header skeleton */}
        <div className="h-12 bg-surface rounded animate-pulse"></div>
        {/* Table rows skeleton - 5 rows × 60px = 300px */}
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-[60px] bg-surface rounded animate-pulse"></div>
        ))}
      </div>
    </div>
  </div>
);

export const PromoCodesSkeleton = () => (
  <div className="w-full magazine-section">
    <div className="elevation-card overflow-hidden modern-card no-hover-lift" data-section="promo-codes">
      {/* Header skeleton - matches actual header height */}
      <div className="px-6 py-6 card-header">
        <div className="h-8 bg-surface rounded animate-pulse w-48 mb-4"></div>
      </div>
      {/* Table skeleton - matches VirtualizedSpendTable height (400px container) */}
      <div className="px-4 py-3 min-h-[400px] space-y-2">
        {/* Table header skeleton */}
        <div className="h-12 bg-surface rounded animate-pulse"></div>
        {/* Table rows skeleton - 5 rows × 60px = 300px */}
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-[60px] bg-surface rounded animate-pulse"></div>
        ))}
      </div>
    </div>
  </div>
);
